<script setup lang="ts">
import { computed, ref } from 'vue';
import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import { erpIntegrationApi } from '@/api/erp-integration';
import { careerLabel } from '@/utils/career-metadata';
import { formatCurrency } from '@/utils/currency';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { formatDateTime, formatMonth } from './erp-format';

// To close a month: what Kuido pays for the Shifts it sends next to the pay of
// Kuido's Sessions in Odoo, as the last nightly reconciliation read them.

const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });
const month = ref<string | undefined>(undefined);

const { data, isPending } = useQuery({
  queryKey: ['erpIntegration', 'totals', month],
  queryFn: () => erpIntegrationApi.totals(month.value),
  placeholderData: keepPreviousData,
});

const months = computed(() => (data.value?.months ?? []).map((value) => ({
  value, title: formatMonth(value),
})));
const rows = computed(() => data.value?.rows ?? []);

const headers = [
  { title: 'Profesional', key: 'caregiver', sortable: false },
  { title: 'Turnos', key: 'shifts' },
  { title: 'Kuido', key: 'kuido' },
  { title: 'Odoo', key: 'erp' },
  { title: 'Diferencia', key: 'difference' },
  { title: 'Sin enviar', key: 'unsent' },
];
</script>
<template>
  <div class="pa-4">
    <h2 class="text-h6 mb-1">
      Cierre de mes
    </h2>
    <p class="mb-4 text-body-2 text-medium-emphasis">
      Lo que Kuido paga por los turnos que envía a Odoo, junto al pago de esas sesiones en Odoo
      según la última reconciliación ({{ formatDateTime(data?.reconciledAt) }}). Una diferencia
      indica sesiones sin enviar, rechazadas o editadas a mano.
    </p>
    <v-select
      :model-value="data?.month"
      :items="months"
      label="Mes"
      variant="outlined"
      density="compact"
      hide-details
      class="mb-4 month-select"
      @update:model-value="(value: string) => (month = value)"
    />
    <v-data-table
      v-bind="tableProps"
      :header-props="{ class: 'font-weight-bold' }"
      :headers="headers"
      :items="rows"
      :loading="isPending"
      loading-text="Cargando los totales..."
      no-data-text="No hay turnos enviados a Odoo este mes"
      density="compact"
    >
      <template #[`item.caregiver`]="{ item }">
        <div>{{ item.caregiver?.name ?? `Empleado ${item.employeeId}` }}</div>
        <div
          v-if="item.caregiver"
          class="text-caption text-medium-emphasis"
        >
          {{ careerLabel(item.caregiver.career) }}
        </div>
      </template>
      <template #[`item.kuido`]="{ item }">
        {{ formatCurrency(item.kuido) }}
      </template>
      <template #[`item.erp`]="{ item }">
        {{ item.erp === null ? 'Sin leer' : formatCurrency(item.erp) }}
      </template>
      <template #[`item.difference`]="{ item }">
        <span v-if="item.difference === null">—</span>
        <v-chip
          v-else-if="Math.abs(item.difference) >= 1"
          size="small"
          color="warning"
          prepend-icon="mdi-alert-circle"
        >
          {{ formatCurrency(item.difference) }}
        </v-chip>
        <v-chip
          v-else
          size="small"
          color="success"
          prepend-icon="mdi-check-circle"
        >
          Cuadra
        </v-chip>
      </template>
    </v-data-table>
  </div>
</template>
<style scoped>
.month-select {
  max-width: 260px;
}
</style>
